import { get, debounce } from 'lodash';
import { PropTypes } from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Query } from '@apollo/client/react/components';

// GraphQL info that the autocomplete needs to work
export const graphqlAutoCompleteType = PropTypes.shape({
  query: PropTypes.shape({}).isRequired, // the gql Query object. Should return an array
  mapper: PropTypes.func.isRequired, // the method that turns the gql results into suggestions
  accessString: PropTypes.string.isRequired, // the string used by _.get to extract the data
  variables: PropTypes.func // a method that takes the current value of the autoselect and returns a varaible passed into the query
});

export const withGraphAutoComplete = () => (Component) => {
  function GraphAutoCompleteHOC(props) {
    const {
      graphql: { mapper = () => {}, variables = () => {}, accessString, query },
      value,
      allSearchValues,
      debounceTime,
      name,
      label,
      characterLimit,
      setFieldValue,
      variant,
      className,
      disabled,
      error,
      errorText,
      onValueChange,
      allowArbitraryInput,
      showAllSuggestions,
      required,
      clearOnBlur,
      onClick,
      styledProps,
      isCustomDropdownPosition
    } = props;

    const [graphVariable, setGraphVariable] = useState(value);

    const updateGraphVariable = useCallback(
      debounce((newGraphVariable) => setGraphVariable(newGraphVariable), debounceTime),
      [debounceTime]
    );

    const { query: searchInput = '' } = value;
    const skip = (searchInput?.length || 0) < (characterLimit || 3);

    return (
      <>
        {/**
         * TO DO: Instead of the Query component, we should use useLazyQuery to ensure debounce functionality works as expected.
         * Currently, whenever a user types in one input, this component re-renders.
         *
         * Written date: 2024-09-23
         * Related User story: 23314
         */}
        <Query skip={skip} query={query} variables={variables(graphVariable)}>
          {({ data, loading }) => {
            const suggestions = mapper(get(data, accessString, []), allSearchValues);
            return (
              <Component
                suggestions={suggestions}
                loading={loading}
                value={value}
                updateGraphVariable={updateGraphVariable}
                debounceTime={debounceTime}
                allowArbitraryInput={allowArbitraryInput}
                name={name}
                label={label}
                setFieldValue={setFieldValue}
                characterLimit={characterLimit}
                variant={variant}
                className={className}
                disabled={disabled}
                error={error}
                errorText={errorText}
                onValueChange={onValueChange}
                showAllSuggestions={showAllSuggestions}
                required={required}
                clearOnBlur={clearOnBlur}
                onClick={onClick}
                styledProps={styledProps}
                isCustomDropdownPosition={isCustomDropdownPosition}
              />
            );
          }}
        </Query>
      </>
    );
  }

  GraphAutoCompleteHOC.propTypes = {
    graphql: graphqlAutoCompleteType,
    debounceTime: PropTypes.number,
    value: PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      query: PropTypes.string
    }), // the current formik value
    allSearchValues: PropTypes.shape({}),
    name: PropTypes.string,
    label: PropTypes.string,
    characterLimit: PropTypes.number,
    setFieldValue: PropTypes.func,
    variant: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    onValueChange: PropTypes.func,
    allowArbitraryInput: PropTypes.bool,
    showAllSuggestions: PropTypes.bool,
    required: PropTypes.bool,
    clearOnBlur: PropTypes.bool,
    onClick: PropTypes.func,
    styledProps: PropTypes.shape({}),
    isCustomDropdownPosition: PropTypes.bool
  };

  GraphAutoCompleteHOC.defaultProps = {
    graphql: {
      query: {},
      mapper: () => [],
      accessString: '',
      variables: () => ({})
    },
    debounceTime: 300,
    value: { value: '', query: '' },
    allSearchValues: {},
    name: '',
    label: '',
    characterLimit: 2,
    setFieldValue: undefined,
    variant: 'outlined',
    className: undefined,
    disabled: false,
    error: false,
    errorText: '',
    onValueChange: undefined,
    allowArbitraryInput: false,
    showAllSuggestions: false,
    required: false,
    clearOnBlur: false,
    onClick: () => {},
    styledProps: {},
    isCustomDropdownPosition: false
  };

  return GraphAutoCompleteHOC;
};
